import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Languages, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/adminApi";

const LANGS = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "ar", label: "العربية" },
];

// Common fields that exist in service content per language. Can be extended.
const TEXT_FIELDS = [
  { key: "hero_eyebrow",       label: "Hero Eyebrow" },
  { key: "hero_title_pre",     label: "Hero Title (öncesi)" },
  { key: "hero_title_accent",  label: "Hero Title (vurgu)" },
  { key: "hero_title_post",    label: "Hero Title (sonrası)" },
  { key: "hero_subtitle",      label: "Hero Subtitle", multiline: true },
  { key: "hero_cta",           label: "Hero CTA Buton" },

  { key: "problems_eyebrow",   label: "Sorunlar Eyebrow" },
  { key: "problems_title",     label: "Sorunlar Başlık" },
  { key: "problem_1",          label: "Sorun #1", multiline: true },
  { key: "problem_2",          label: "Sorun #2", multiline: true },
  { key: "problem_3",          label: "Sorun #3", multiline: true },
  { key: "problem_4",          label: "Sorun #4", multiline: true },

  { key: "solutions_eyebrow",  label: "Çözümler Eyebrow" },
  { key: "solutions_title",    label: "Çözümler Başlık" },
  { key: "solution_1",         label: "Çözüm #1", multiline: true },
  { key: "solution_2",         label: "Çözüm #2", multiline: true },
  { key: "solution_3",         label: "Çözüm #3", multiline: true },
  { key: "solution_4",         label: "Çözüm #4", multiline: true },

  { key: "process_eyebrow",    label: "Süreç Eyebrow" },
  { key: "process_title",      label: "Süreç Başlık" },
  { key: "process_step_1",     label: "Adım 1", multiline: true },
  { key: "process_step_2",     label: "Adım 2", multiline: true },
  { key: "process_step_3",     label: "Adım 3", multiline: true },
  { key: "process_step_4",     label: "Adım 4", multiline: true },

  { key: "pricing_eyebrow",    label: "Pricing Eyebrow" },
  { key: "pricing_title",      label: "Pricing Başlık" },
  { key: "pricing_note",       label: "Pricing Not" },
  { key: "pricing_cta",        label: "Pricing CTA" },
  { key: "pricing_pro_badge",  label: "Pricing Pro Badge" },
  { key: "tier_starter_name",  label: "Tier Starter İsim" },
  { key: "tier_starter_desc",  label: "Tier Starter Açıklama", multiline: true },
  { key: "tier_pro_name",      label: "Tier Pro İsim" },
  { key: "tier_pro_desc",      label: "Tier Pro Açıklama", multiline: true },
  { key: "tier_premium_name",  label: "Tier Premium İsim" },
  { key: "tier_premium_desc",  label: "Tier Premium Açıklama", multiline: true },

  { key: "case_study_eyebrow", label: "Vaka Çalışması Eyebrow" },
  { key: "case_study_title",   label: "Vaka Çalışması Başlık" },
  { key: "case_study_body",    label: "Vaka Çalışması Metin", multiline: true },

  { key: "faq_eyebrow",        label: "FAQ Eyebrow" },
  { key: "faq_title",          label: "FAQ Başlık" },
  { key: "faq_q1",             label: "FAQ Soru 1" },
  { key: "faq_a1",             label: "FAQ Cevap 1", multiline: true },
  { key: "faq_q2",             label: "FAQ Soru 2" },
  { key: "faq_a2",             label: "FAQ Cevap 2", multiline: true },
  { key: "faq_q3",             label: "FAQ Soru 3" },
  { key: "faq_a3",             label: "FAQ Cevap 3", multiline: true },

  { key: "cta_title",          label: "Bottom CTA Başlık" },
  { key: "cta_subtitle",       label: "Bottom CTA Alt Başlık", multiline: true },
];

/**
 * Textarea that keeps the raw text while typing (so Enter creates a new line)
 * and only splits into an array of features on blur. Fixes the bug where
 * `split("\n").filter(Boolean)` on every keystroke swallowed the new line.
 */
function FeaturesTextarea({ value, onChange, placeholder, testId }) {
  const [text, setText] = useState((value || []).join("\n"));
  useEffect(() => { setText((value || []).join("\n")); }, [value]);
  const commit = () => onChange(text.split("\n").map((l) => l.trim()).filter(Boolean));
  return (
    <textarea
      rows={5}
      className="input-base resize-y text-sm font-mono"
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      data-testid={testId}
    />
  );
}

export default function AdminServiceEditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [activeLang, setActiveLang] = useState("tr");
  const [activeTab, setActiveTab] = useState("content"); // content | pricing | meta
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] = useState(null); // { current: "en", done: 2, total: 5, field: "hero_subtitle" }
  const serviceRef = useRef(null);
  useEffect(() => { serviceRef.current = service; }, [service]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/services/${slug}`);
      // Ensure content has all langs initialized as objects
      const content = { ...(data.content || {}) };
      LANGS.forEach((l) => { if (!content[l.code]) content[l.code] = {}; });
      setService({ ...data, content });
    } catch (e) {
      toast.error("Hizmet yüklenemedi");
      navigate("/admin/services");
    } finally { setLoading(false); }
  }, [slug, navigate]);

  useEffect(() => { load(); }, [load]);

  const setField = (lang, key, val) => {
    setService((s) => ({
      ...s,
      content: { ...s.content, [lang]: { ...(s.content[lang] || {}), [key]: val } },
    }));
  };

  const setTopLevel = (k, v) => setService((s) => ({ ...s, [k]: v }));

  const setPricingField = (idx, k, v) => {
    setService((s) => {
      const next = [...(s.pricing || [])];
      next[idx] = { ...next[idx], [k]: v };
      return { ...s, pricing: next };
    });
  };

  const addPricingTier = () => {
    setService((s) => ({
      ...s,
      pricing: [...(s.pricing || []), { id: "starter", price_try: 10000, popular: false }],
    }));
  };

  const removePricingTier = (idx) => {
    if (!window.confirm("Bu fiyat paketini silmek istediğine emin misin?")) return;
    setService((s) => ({ ...s, pricing: (s.pricing || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    // Flush any textarea that is still focused (features are committed on blur)
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
      await new Promise((r) => setTimeout(r, 0));
    }
    setSaving(true);
    try {
      const current = serviceRef.current || service;
      // Recompute translated_langs from content (lang has any non-empty string field)
      const translated = LANGS
        .map((l) => l.code)
        .filter((code) => Object.values(current.content[code] || {}).some((v) => typeof v === "string" && v.trim()));
      const payload = { ...current, translated_langs: translated };
      delete payload.created_at;
      delete payload.updated_at;
      const { data } = await api.put(`/admin/services/${slug}`, payload);
      const content = { ...(data.content || {}) };
      LANGS.forEach((l) => { if (!content[l.code]) content[l.code] = {}; });
      setService({ ...data, content });
      toast.success("Kaydedildi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt hatası"); }
    finally { setSaving(false); }
  };

  const aiTranslate = async () => {
    if (!Object.keys(service.content.tr || {}).length) {
      toast.error("Önce TR içeriği doldur ve kaydet."); return;
    }
    if (!window.confirm("TR içeriği AI ile 5 dile çevirip mevcut çevirilerin üzerine yazmak istediğine emin misin?")) return;
    setTranslating(true);

    const targetLangs = ["en", "es", "de", "fr", "ar"];
    const langNames  = { en: "İngilizce", es: "İspanyolca", de: "Almanca", fr: "Fransızca", ar: "Arapça" };
    const trDict = service.content.tr || {};
    // Only translate non-empty string fields
    const fields = Object.entries(trDict).filter(([, v]) => typeof v === "string" && v.trim().length > 0);
    const totalSteps = targetLangs.length;
    let successCount = 0;

    setTranslateProgress({ current: null, done: 0, total: totalSteps, field: "Başlıyor…" });

    const mergedTranslations = {};

    for (let li = 0; li < targetLangs.length; li++) {
      const lang = targetLangs[li];
      setTranslateProgress({
        current: lang,
        done: li,
        total: totalSteps,
        field: `${langNames[lang]} çevriliyor… (${fields.length} alan)`,
      });

      mergedTranslations[lang] = {};
      let langSuccess = true;

      for (let fi = 0; fi < fields.length; fi++) {
        const [k, v] = fields[fi];
        if (fi % 3 === 0) {
          setTranslateProgress({
            current: lang,
            done: li,
            total: totalSteps,
            field: `${langNames[lang]}: ${fi + 1}/${fields.length} alan`,
          });
        }
        try {
          const r = await api.post("/translate", {
            text: v,
            source_lang: "tr",
            target_langs: [lang],
          });
          const translated = (r.data?.translations || {})[lang];
          mergedTranslations[lang][k] = translated || v;
        } catch (fieldErr) {
          // On rate limit or budget error, stop this language
          const errMsg = fieldErr?.response?.data?.detail;
          const detail = typeof errMsg === "string" ? errMsg
            : Array.isArray(errMsg) ? errMsg.map(e => e.msg).join(", ")
            : (fieldErr?.message || "Bilinmeyen hata");
          if (detail.toLowerCase().includes("budget") || detail.toLowerCase().includes("rate")) {
            toast.error(`${langNames[lang]}: Limit aşıldı — ${detail}`);
            langSuccess = false;
            break;
          }
          // Non-fatal: keep original text
          mergedTranslations[lang][k] = v;
        }
      }

      if (langSuccess) successCount++;
    }

    setTranslateProgress({ current: null, done: totalSteps, total: totalSteps, field: `Tamamlandı! (${successCount}/${totalSteps} dil)` });

    // Apply all translations
    setService((s) => {
      const next = { ...s.content };
      for (const [lang, dict] of Object.entries(mergedTranslations)) {
        if (Object.keys(dict).length > 0) {
          next[lang] = { ...(next[lang] || {}), ...dict };
        }
      }
      return { ...s, content: next };
    });

    if (successCount === totalSteps) {
      toast.success("AI çeviri tamamlandı — Kaydet'e basmayı unutma");
    } else {
      toast.warning(`${successCount}/${totalSteps} dil çevrildi — Kaydet'e bas, eksikleri sonra tamamla`);
    }
    setTranslating(false);
    setTimeout(() => setTranslateProgress(null), 4000);
  };

  if (loading || !service) {
    return <div className="p-10 text-ink/50"><Loader2 className="h-5 w-5 animate-spin inline" /> Yükleniyor…</div>;
  }

  const langContent = service.content[activeLang] || {};

  return (
    <div data-testid={`service-edit-${slug}`}>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <Link to="/admin/services" className="inline-flex items-center gap-1 text-xs text-ink/55 hover:text-ink mb-2"
            data-testid="service-edit-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Hizmetlere dön
          </Link>
          <h1 className="font-display text-3xl font-bold">{slug}</h1>
          <p className="mt-1 text-xs text-ink/50">URL: /:lang/services/{slug}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            <button type="button" onClick={aiTranslate} disabled={translating}
              className="btn-outline-ink !text-xs !py-2.5 inline-flex items-center gap-2"
              data-testid="service-edit-translate">
              {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
              {translating ? "Çevriliyor…" : "TR → 5 dil (AI)"}
            </button>
            <button type="button" onClick={save} disabled={saving}
              className="btn-gold !text-xs !py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="service-edit-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Kaydet
            </button>
          </div>

          {/* Translation progress bar */}
          {translateProgress && (
            <div className="w-full max-w-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[0.65rem] text-ink/60 truncate max-w-[220px]">{translateProgress.field}</span>
                <span className="text-[0.65rem] text-ink/50 ml-2">{translateProgress.done}/{translateProgress.total}</span>
              </div>
              <div className="h-1.5 rounded-full bg-mist overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-brand-pink to-brand-magenta transition-all duration-500"
                  style={{ width: `${(translateProgress.done / translateProgress.total) * 100}%` }}
                />
              </div>
              <p className="text-[0.6rem] text-ink/40 mt-1">Sayfayı kapatmayın</p>
            </div>
          )}
        </div>
      </div>

      {/* Top-level meta (icon, order, review tags) */}
      <div className="rounded-2xl bg-white border border-mist p-5 mb-6 grid sm:grid-cols-3 gap-4">
        <div>
          <label className="label-base">Icon (Lucide)</label>
          <input className="input-base" value={service.icon || ""} onChange={(e) => setTopLevel("icon", e.target.value)}
            data-testid="service-edit-icon" />
        </div>
        <div>
          <label className="label-base">Sıra</label>
          <input type="number" className="input-base" value={service.order ?? 99}
            onChange={(e) => setTopLevel("order", Number(e.target.value))}
            data-testid="service-edit-order" />
        </div>
        <div>
          <label className="label-base">Review Tags (virgülle)</label>
          <input className="input-base"
            value={(service.review_tags || []).join(", ")}
            onChange={(e) => setTopLevel("review_tags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
            data-testid="service-edit-tags" />
        </div>
      </div>

      {/* Tab strip: Content (per-lang) / Pricing */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-mist pb-3">
        {[{k:"content",l:"İçerik"}, {k:"pricing",l:"Fiyatlandırma"}].map((t) => (
          <button key={t.k} type="button" onClick={() => setActiveTab(t.k)}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] rounded-md
              ${activeTab === t.k ? "bg-ink text-cream" : "text-ink/55 hover:text-ink"}`}
            data-testid={`service-edit-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {activeTab === "content" && (
        <>
          {/* 6-lang tabs */}
          <div className="flex flex-wrap gap-2 mb-5">
            {LANGS.map((l) => {
              const filled = Object.values(service.content[l.code] || {}).some((v) => typeof v === "string" && v.trim());
              return (
                <button key={l.code} type="button" onClick={() => setActiveLang(l.code)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors inline-flex items-center gap-2
                    ${l.code === activeLang ? "bg-brand-pink text-cream" : "bg-mist text-ink/70 hover:bg-mist/70"}`}
                  data-testid={`service-edit-lang-${l.code}`}>
                  {l.code.toUpperCase()} <span className="text-[10px] opacity-70">{l.label}</span>
                  {filled && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                </button>
              );
            })}
          </div>

          <div className="rounded-2xl bg-white border border-mist p-6 sm:p-7 grid gap-5 sm:grid-cols-2 max-w-5xl">
            {TEXT_FIELDS.map((f) => (
              <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
                <label className="label-base">{f.label} <span className="text-ink/30 font-normal">· {f.key}</span></label>
                {f.multiline ? (
                  <textarea rows={3} className="input-base resize-y"
                    value={langContent[f.key] || ""}
                    onChange={(e) => setField(activeLang, f.key, e.target.value)}
                    data-testid={`service-edit-${activeLang}-${f.key}`} />
                ) : (
                  <input className="input-base"
                    value={langContent[f.key] || ""}
                    onChange={(e) => setField(activeLang, f.key, e.target.value)}
                    data-testid={`service-edit-${activeLang}-${f.key}`} />
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === "pricing" && (
        <div className="space-y-4">
          {/* Pricing language selector + translate button */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 flex-wrap bg-white border border-mist rounded-xl p-1">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setActiveLang(l.code)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition-colors ${
                    activeLang === l.code ? "bg-ink text-cream" : "text-ink/60 hover:text-ink"
                  }`}
                >
                  {l.code.toUpperCase()}
                </button>
              ))}
            </div>
            {/* Pricing features translate button — ONLY updates pricing.features_XX */}
            <button
              type="button"
              disabled={translating}
              onClick={async () => {
                const trTiers = (service.pricing || []).filter(t => t.features?.length || t.features_tr?.length);
                if (!trTiers.length) { toast.error("Önce TR özelliklerini gir ve kaydet"); return; }
                if (!window.confirm("TR paket özelliklerini ES, DE, FR, AR, EN dillerine çevireyim mi?\n\nSadece paket özellikleri güncellenir, içerik sekmesi etkilenmez.")) return;
                setTranslating(true);

                const langs = ["en", "es", "de", "fr", "ar"];
                const langNames = { en: "İngilizce", es: "İspanyolca", de: "Almanca", fr: "Fransızca", ar: "Arapça" };

                // ── Accumulate all language results before updating state once ──
                // Start from current pricing, add features_XX for each lang
                let accPricing = (service.pricing || []).map(t => ({ ...t }));
                let doneCount = 0;
                let budgetExceeded = false;

                for (let li = 0; li < langs.length; li++) {
                  if (budgetExceeded) break;
                  const lang = langs[li];

                  setTranslateProgress({
                    current: lang, done: li, total: langs.length,
                    field: `${langNames[lang]} çevriliyor… (${accPricing.length} paket × özellikleri)`,
                  });

                  for (let ti = 0; ti < accPricing.length; ti++) {
                    const tier = accPricing[ti];
                    const features = (tier.features_tr?.length ? tier.features_tr : tier.features) || [];
                    if (!features.length) continue;

                    const translatedFeats = [];
                    for (const feat of features) {
                      if (budgetExceeded) break;
                      try {
                        const r = await api.post("/translate", {
                          text: feat, source_lang: "tr", target_langs: [lang],
                        });
                        translatedFeats.push(r.data?.translations?.[lang] || feat);
                      } catch (err) {
                        const detail = err?.response?.data?.detail;
                        const msg = typeof detail === "string" ? detail
                          : Array.isArray(detail) ? detail.map(d => d.msg).join(", ")
                          : (err?.message || "hata");
                        // Detect any quota/limit/budget error
                        const isLimit = /budget|limit|quota|exceeded|rate/i.test(msg);
                        if (isLimit) {
                          toast.error(`Limit aşıldı (${doneCount}/${langs.length} dil tamamlandı)`);
                          budgetExceeded = true;
                          break;
                        }
                        translatedFeats.push(feat); // non-fatal: keep TR
                      }
                    }
                    if (!budgetExceeded) {
                      accPricing[ti] = { ...tier, [`features_${lang}`]: translatedFeats };
                    }
                  }

                  if (!budgetExceeded) doneCount++;
                }

                // ── Single state update with ALL languages at once ──
                setService(s => ({ ...s, pricing: accPricing }));
                setTranslateProgress({
                  current: null, done: langs.length, total: langs.length,
                  field: budgetExceeded
                    ? `Kısmi çeviri (${doneCount}/${langs.length} dil) — Kaydet'e bas`
                    : `Tamamlandı! (${doneCount}/${langs.length} dil) — Kaydet'e bas`,
                });

                if (!budgetExceeded) toast.success("Paket özellikleri çevrildi — Kaydet'e bas");
                setTranslating(false);
                setTimeout(() => setTranslateProgress(null), 4000);
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-pink/40 px-3 py-1.5 text-xs font-semibold text-brand-pink hover:bg-brand-pink hover:text-cream transition-colors disabled:opacity-60"
            >
              {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
              {translating ? "Çevriliyor…" : "Paket özelliklerini 5 dile çevir"}
            </button>
          </div>

          {/* Progress bar for pricing translate */}
          {translateProgress && (
            <div className="w-full max-w-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[0.65rem] text-ink/60 truncate max-w-[220px]">{translateProgress.field}</span>
                <span className="text-[0.65rem] text-ink/50 ml-2">{translateProgress.done}/{translateProgress.total}</span>
              </div>
              <div className="h-1.5 rounded-full bg-mist overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-brand-pink to-brand-magenta transition-all duration-500"
                  style={{ width: `${(translateProgress.done / translateProgress.total) * 100}%` }} />
              </div>
            </div>
          )}

          <p className="text-xs text-ink/50">
            Fiyatlar TRY olarak girilir. Dil butonları ile her dilin paket özelliklerini ayrı düzenleyin.
          </p>

          {(service.pricing || []).map((tier, i) => {
            // Get features for current language, fallback to TR
            const featuresKey = `features_${activeLang}`;
            const featuresDefault = `features_tr`;
            const currentFeatures =
              tier[featuresKey] ||
              (activeLang === "tr" ? tier.features || [] : tier[featuresDefault] || tier.features || []);

            const setFeaturesForLang = (val) => {
              setPricingField(i, featuresKey, val);
              // Also update main features if TR
              if (activeLang === "tr") {
                setPricingField(i, "features", val);
              }
            };

            return (
              <div key={i} className="rounded-2xl bg-white border border-mist p-5 space-y-4"
                data-testid={`service-pricing-row-${i}`}>
                <div className="grid gap-4 sm:grid-cols-12 items-end">
                  <div className="sm:col-span-3">
                    <label className="label-base">Tier ID</label>
                    <select className="input-base" value={tier.id || "starter"}
                      onChange={(e) => setPricingField(i, "id", e.target.value)}
                      data-testid={`service-pricing-tier-id-${i}`}>
                      {slug === "postun-hazir" ? (
                        <>
                          <option value="starter">starter (Başlangıç)</option>
                          <option value="growth">growth (Büyüme)</option>
                          <option value="scale">scale (Ölçek)</option>
                        </>
                      ) : (
                        <>
                          <option value="starter">starter (Başlangıç)</option>
                          <option value="pro">pro (Profesyonel)</option>
                          <option value="premium">premium (Premium)</option>
                        </>
                      )}
                      {tier.id && !["starter", "pro", "premium", "growth", "scale"].includes(tier.id) && (
                        <option value={tier.id}>{tier.id}</option>
                      )}
                    </select>
                  </div>
                  <div className="sm:col-span-3">
                    <label className="label-base">Fiyat (TRY/ay)</label>
                    <input type="number" min={0} step={100} className="input-base" value={tier.price_try ?? 0}
                      onChange={(e) => setPricingField(i, "price_try", e.target.value === "" ? 0 : Number(e.target.value))}
                      data-testid={`service-pricing-price-${i}`} />
                  </div>
                  <div className="sm:col-span-3 flex items-center gap-2 pt-6">
                    <input id={`popular-${i}`} type="checkbox" checked={!!tier.popular}
                      onChange={(e) => setPricingField(i, "popular", e.target.checked)} />
                    <label htmlFor={`popular-${i}`} className="text-sm text-ink/70">En çok tercih edilen</label>
                  </div>
                  <div className="sm:col-span-3 text-right pt-5">
                    <button type="button" onClick={() => removePricingTier(i)}
                      className="inline-flex items-center gap-1.5 text-xs text-destructive hover:text-destructive/80">
                      <Trash2 className="h-3.5 w-3.5" /> Sil
                    </button>
                  </div>
                </div>

                {/* Package features — per language */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="label-base">
                      Paket Özellikleri
                      <span className="ml-2 rounded-full bg-ink text-cream text-[0.6rem] px-2 py-0.5 font-bold">
                        {activeLang.toUpperCase()}
                      </span>
                    </label>
                    {activeLang !== "tr" && !(tier[featuresKey]?.length) && (
                      <button
                        type="button"
                        className="text-xs text-brand-pink hover:underline"
                        onClick={() => setFeaturesForLang([...(tier.features || [])])}
                      >
                        TR'den kopyala →
                      </button>
                    )}
                  </div>
                  <FeaturesTextarea
                    key={`${i}-${activeLang}`}
                    value={currentFeatures}
                    onChange={setFeaturesForLang}
                    placeholder={activeLang === "tr"
                      ? "Ayda 8 post + 4 video kreatif\n1 reklam hesabı\nAylık rapor"
                      : "Her satır bir özellik (bu dil için)"}
                    testId={`service-pricing-features-${i}-${activeLang}`}
                  />
                  <p className="mt-1 text-xs text-ink/45">
                    Her satır ayrı bir özellik. Boş satırları atlayın.
                    {activeLang !== "tr" && " • Bu dil için boş bırakırsanız TR özellikleri gösterilir."}
                  </p>
                </div>
              </div>
            );
          })}
          <button type="button" onClick={addPricingTier}
            className="btn-outline-ink !text-xs !py-2.5 inline-flex items-center gap-2"
            data-testid="service-pricing-add">
            <Plus className="h-4 w-4" /> Paket Ekle
          </button>
        </div>
      )}
    </div>
  );
}
